import { CommonResponse, get } from '../requestNew';

export interface SeasonOption {
  gameSession: string;
  gameSessionName: string;
  inProgress?: boolean;
}

export interface WorkbenchOverview {
  updatedAt: string;
  queryDate: string;
  days: number;
  session: string;
  seasons: SeasonOption[];
  currentGame: {
    gameSession: string;
    gameSessionName: string;
    gameStatus: string;
    gameStatusText: string;
    gameStartTime: string;
    gameEndTime: string;
    participantCount: number;
    groupCount: number;
  };
  today: {
    reportedUsers: number;
    previousReportedUsers: number;
    reportedUserChange: number;
    expectedUsers: number;
    unreportedUsers: number;
    completionRate: number;
    pendingOrders: number;
    overdueOrders: number;
    abnormalOrders: number;
    activeUsers: number;
    activeUserChangeRate: number | null;
  };
  todo: {
    pendingOrders: number;
    abnormalOrders: number;
    penaltyPending: number;
    reportCountToday: number;
    riskCommentPending: number;
    rankCommentPending: number;
  };
  groups: Array<{
    groupType: number;
    name: string;
    expected: number;
    reported: number;
    completionRate: number;
    pending: number;
    abnormal: number;
  }>;
  queue: Array<{
    id: number;
    openId: string;
    gameName: string;
    groupType: number;
    groupName: string;
    issueSummary: string;
    todayEarningRate: number;
    waitingMinutes: number;
    priority: string;
    createTime: string;
  }>;
  dataWarnings: string[];
}

export async function fetchWorkbenchOverview(params: { days: number; session?: string }) {
  const response = await get<CommonResponse<WorkbenchOverview>>('/workbench/overview', params);
  if (response.code !== 200) {
    throw new Error(response.message || '数据加载失败');
  }
  return response.data;
}

export interface WorkbenchDashboard {
  updatedAt: string;
  range: string;
  session: string;
  sessionName: string;
  seasonFinished: boolean;
  startDate: string;
  endDate: string;
  metrics: {
    activeUsers: number;
    activeChangeRate: number | null;
    newUsers: number;
    previousNewUsers: number;
    newUserChangeRate: number | null;
    completionRate: number | null;
    completionDelta: number | null;
    interactions: number;
    interactionChangeRate: number | null;
  };
  unfinishedUsers: number;
  trend: Array<{ label: string; value: number }>;
  conversion: {
    visitors: number | null;
    registrations: number;
    approved: number;
    firstReports: number;
    visitorToFirstReportRate: number | null;
  };
  groups: Array<{
    groupType: string;
    name: string;
    expected: number;
    reported: number;
    completionRate: number | null;
    pending: number;
    abnormal: number;
  }>;
  groupTrend: {
    labels: string[];
    series: Array<{ groupType: string; name: string; values: Array<number | null> }>;
  } | null;
  content: {
    notes: number;
    comments: number;
    authors: number;
    likes: number;
    interactions: number;
  };
  attention: {
    abnormalUnreviewed: number;
    unfinishedUsers: number;
    topGroupName: string | null;
    topGroupUnfinished: number | null;
    topGroupCompletionRate: number | null;
  };
  cohorts: Array<{
    batch: string;
    startDate: string;
    endDate: string;
    newUsers: number;
    nextDayRetention: number | null;
    sevenDayRetention: number | null;
    sevenDaySignupRate: number | null;
  }>;
  dataWarnings: string[];
}

export async function fetchWorkbenchDashboard(params: {
  range: string;
  session?: string;
  refresh?: boolean;
}) {
  const response = await get<CommonResponse<WorkbenchDashboard>>('/workbench/dashboard', params);
  if (response.code !== 200) {
    throw new Error(response.message || '数据加载失败');
  }
  return response.data;
}

export function formatNumber(value?: number | null) {
  return value === null || value === undefined ? '—' : value.toLocaleString('zh-CN');
}

export function formatPercent(value?: number | null, digits = 1) {
  return value === null || value === undefined ? '—' : `${value.toFixed(digits)}%`;
}

export function formatDateTime(value?: string | null) {
  return value ? value.replace('T', ' ').slice(0, 16) : '—';
}

export function formatWaiting(minutes?: number | null) {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${minutes} 分钟`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} 小时 ${rest} 分` : `${hours} 小时`;
}
